/**
 * Hooks React Query pour les analytics personnelles
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import * as analyticsService from '../services/analytics.service';
import type {
  UserSummary,
  RecentAttempt,
} from '@sphere/analytics/domain';

export const personalAnalyticsKeys = {
  all: ['analytics', 'personal'] as const,
  dashboard: (userId: string, sinceDays: number) =>
    [...personalAnalyticsKeys.all, 'dashboard', userId, sinceDays] as const,
  topActivities: (userId: string, sinceDays: number) =>
    [...personalAnalyticsKeys.all, 'top-activities', userId, sinceDays] as const,
  recentAttempts: (userId: string, sinceDays: number, limit: number) =>
    [...personalAnalyticsKeys.all, 'recent-attempts', userId, sinceDays, limit] as const,
  engagement: (userId: string, sinceDays: number) =>
    [...personalAnalyticsKeys.all, 'engagement', userId, sinceDays] as const,
};

export function usePersonalDashboard(
  userId: string | undefined,
  sinceDays = 30,
  options?: Omit<UseQueryOptions<UserSummary>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: personalAnalyticsKeys.dashboard(userId!, sinceDays),
    queryFn: () => analyticsService.getUserProgressSummary(userId!, { since_days: sinceDays }),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
}

export function usePersonalRecentAttempts(
  userId: string | undefined,
  sinceDays = 30,
  limit = 200,
  options?: Omit<
    UseQueryOptions<{
      user_id: string;
      since_days: number;
      attempts: RecentAttempt[];
    }>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: personalAnalyticsKeys.recentAttempts(userId!, sinceDays, limit),
    queryFn: () => analyticsService.getRecentAttempts(userId!, { since_days: sinceDays, limit }),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
}

export function usePersonalTopActivities(
  userId: string | undefined,
  sinceDays = 30,
  options?: Omit<
    UseQueryOptions<{
      user_id: string;
      since_days: number;
      attempts: RecentAttempt[];
    }>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: personalAnalyticsKeys.topActivities(userId!, sinceDays),
    queryFn: () => analyticsService.getRecentAttempts(userId!, { since_days: sinceDays, limit: 200 }),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
}

export function usePersonalEngagementMetrics(
  userId: string | undefined,
  sinceDays = 30,
  options?: Omit<
    UseQueryOptions<{
      user_id: string;
      since_days: number;
      attempts: RecentAttempt[];
    }>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: personalAnalyticsKeys.engagement(userId!, sinceDays),
    queryFn: () => analyticsService.getRecentAttempts(userId!, { since_days: sinceDays, limit: 200 }),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
}
